if (!customElements.get('axiometa-catalog')) {
  customElements.define('axiometa-catalog', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.ready = false;
      this.events = new AbortController();
      this.timers = new Set();
      this.addQueue = Promise.resolve();
      this.summaryRevision = 0;
      this.defaultLayout = this.dataset.defaultLayout === 'compact' ? 'compact' : 'grid';
      this.allowViewSwitch = this.dataset.viewSwitch !== 'false';
      this.desktopColumns = Number(this.dataset.columns) || 4;
      this.grid = this.querySelector('[data-catalog-grid]');
      this.stockInput = this.querySelector('[data-in-stock]');
      this.categoryButtons = [...this.querySelectorAll('[data-category]')];
      this.categories = new Map(this.categoryButtons.map(button => [button.dataset.category, {
        ids: new Set((button.dataset.productIds || '').split(',')),
        tag: this.normalize(button.dataset.tag || ''),
      }]));
      this.listen(this, 'submit', event => this.onSubmit(event));
      this.listen(this, 'click', event => this.onClick(event));
      this.readCards();
      this.syncCartStyle();
      this.listen(document, 'shopify:section:load', () => this.syncCartStyle());
      this.updateCartBadges();
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
          if (event.source !== 'catalog') return this.refreshCartSummary();
        });
      }
      this.prepareCatalog();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.unsubscribeCart?.();
      this.timers?.forEach(timer => clearTimeout(timer));
      this.initialized = false;
    }

    listen(target, type, handler) {
      target.addEventListener(type, handler, { signal: this.events.signal });
    }

    normalize(value) {
      return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
    }

    syncCartStyle() {
      const header = document.querySelector('site-header');
      if (!header) return;
      const style = getComputedStyle(header);
      const properties = {
        '--header-background': '--catalog-cart-background',
        '--header-text': '--catalog-cart-text',
        '--header-button-background': '--catalog-cart-button-background',
        '--header-button-text': '--catalog-cart-button-text',
        '--header-radius': '--catalog-cart-radius',
        '--header-button-radius': '--catalog-cart-button-radius',
      };
      for (const [source, target] of Object.entries(properties)) {
        const value = style.getPropertyValue(source).trim();
        if (value) this.style.setProperty(target, value);
      }
    }

    readCards() {
      this.cards = [...this.grid.querySelectorAll('[data-catalog-product]')].map((element, index) => {
        const data = element.dataset;
        let tags = [];
        try { tags = JSON.parse(data.tags || '[]'); } catch (_) { /* Untagged product. */ }
        return {
          element, index, id: data.productId,
          available: data.available === 'true',
          tags: tags.map(tag => this.normalize(tag)),
        };
      });
    }

    async prepareCatalog() {
      // Load every server page before enabling client filters, so matches never
      // depend on which cards have already been displayed.
      try {
        let next = this.querySelector('[data-catalog-next]')?.getAttribute('href');
        const visited = new Set();
        const fromFirstPage = Number(this.grid.dataset.page) > 1;
        const incoming = document.createDocumentFragment();
        const productIds = new Set(fromFirstPage ? [] : this.cards.map(card => card.id));
        if (fromFirstPage) {
          const first = new URL(location.href);
          first.searchParams.delete('page');
          next = first.href;
        }
        while (next) {
          const url = new URL(next, location.href);
          url.searchParams.set('section_id', this.dataset.sectionId);
          if (visited.has(url.href)) throw new Error('Repeated catalog page');
          visited.add(url.href);
          const response = await fetch(url, { signal: this.events.signal });
          if (!response.ok) throw new Error('Catalog page unavailable');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const catalog = doc.querySelector('axiometa-catalog');
          if (!catalog) throw new Error('Missing catalog section');
          for (const card of catalog.querySelectorAll('[data-catalog-product]')) {
            if (productIds.has(card.dataset.productId)) continue;
            productIds.add(card.dataset.productId);
            incoming.append(card);
          }
          next = catalog.querySelector('[data-catalog-next]')?.getAttribute('href');
        }
        if (!this.isConnected) return;
        if (fromFirstPage) this.grid.replaceChildren(incoming);
        else this.grid.append(incoming);
        this.readCards();
        this.ready = true;
        this.querySelectorAll('[data-catalog-enhanced]').forEach(element => { element.hidden = false; });
        this.querySelectorAll('[data-server-pagination]').forEach(element => { element.hidden = true; });
        this.restoreState();
        this.listen(this.stockInput, 'change', () => this.applyFilters(true));
        this.listen(window, 'popstate', () => this.restoreState());
        this.listen(document, 'shopify:block:select', event => {
          if (!this.contains(event.target)) return;
          const category = event.target.closest('[data-category]');
          if (category) { this.category = category.dataset.category; this.applyFilters(); }
        });
        this.updateCartBadges();
      } catch (error) {
        if (error.name === 'AbortError') return;
        const notice = this.querySelector('[data-catalog-load-error]');
        notice.textContent = 'Some browsing controls could not load. You can still view products, search the store and add to cart.';
        notice.hidden = false;
      }
    }

    restoreState() {
      const params = new URL(location.href).searchParams;
      this.stockInput.checked = params.get('stock') === '1';
      this.category = this.categories.has(params.get('category')) ? params.get('category') : 'all';
      const requestedView = params.get('catalog_view') || params.get('view');
      this.dataset.layout = this.dataset.designMode !== 'true' && this.allowViewSwitch && ['grid', 'compact'].includes(requestedView)
        ? requestedView : this.defaultLayout;
      this.applyFilters();
      // Keep the selected tab visible in the horizontally scrolling mobile rows.
      for (const selector of ['.catalog__collections [aria-current]', '[data-category][aria-pressed="true"]']) {
        const active = this.querySelector(selector);
        const row = active?.parentElement;
        if (row && row.scrollWidth > row.clientWidth) {
          row.scrollLeft += active.getBoundingClientRect().left - row.getBoundingClientRect().left - (row.clientWidth - active.offsetWidth) / 2;
        }
      }
    }

    matchesCategory(card, category) {
      if (category === 'all') return true;
      const filter = this.categories.get(category);
      return !!filter && (filter.ids.has(card.id) || (filter.tag && card.tags.includes(filter.tag)));
    }

    applyFilters(historyMode) {
      if (!this.ready) return;
      const candidates = this.cards.filter(card => !this.stockInput.checked || card.available);
      this.categoryButtons.forEach(button => {
        const category = button.dataset.category;
        const count = candidates.filter(card => this.matchesCategory(card, category)).length;
        button.querySelector('[data-category-count]').textContent = count;
        button.setAttribute('aria-pressed', String(category === this.category));
        button.disabled = count === 0 && category !== this.category && category !== 'all';
      });
      this.filteredCards = candidates.filter(card => this.matchesCategory(card, this.category));
      const columns = this.dataset.adaptColumns !== 'false' && this.filteredCards.length < 4
        ? Math.min(this.desktopColumns, 3) : this.desktopColumns;
      this.style.setProperty('--catalog-columns', columns);
      this.cards.forEach(card => { card.element.hidden = true; });
      const fragment = document.createDocumentFragment();
      this.filteredCards.forEach(card => {
        card.element.hidden = false;
        fragment.append(card.element);
      });
      this.grid.append(fragment);
      this.querySelector('[data-catalog-empty]').hidden = this.filteredCards.length > 0;
      this.querySelectorAll('[data-view]').forEach(button => {
        button.setAttribute('aria-pressed', String(button.dataset.view === this.dataset.layout));
      });
      if (historyMode) this.saveState(historyMode);
    }

    saveState(mode) {
      const url = new URL(location.href);
      const values = {
        category: this.category === 'all' ? '' : this.category,
        stock: this.stockInput.checked ? '1' : '',
        catalog_view: this.dataset.layout === this.defaultLayout ? '' : this.dataset.layout,
      };
      Object.entries(values).forEach(([key, value]) => value ? url.searchParams.set(key, value) : url.searchParams.delete(key));
      url.searchParams.delete('catalog_sort');
      if (['grid', 'compact'].includes(url.searchParams.get('view'))) url.searchParams.delete('view');
      // All pages are loaded above, so a shared filtered URL starts from page one.
      url.searchParams.delete('page');
      if (url.href !== location.href) history[mode === 'replace' ? 'replaceState' : 'pushState']({}, '', url);
    }

    onClick(event) {
      const review = event.target.closest('[data-review-cart]');
      if (review) {
        const drawer = document.querySelector('cart-drawer');
        if (drawer?.open && this.drawerCurrent !== false) { event.preventDefault(); drawer.open(review); }
        return;
      }
      if (!this.ready) return;
      const category = event.target.closest('[data-category]');
      const view = event.target.closest('[data-view]');
      if (category) { this.category = category.dataset.category; this.applyFilters(true); }
      if (view && this.allowViewSwitch) { this.dataset.layout = view.dataset.view; this.applyFilters(true); }
    }

    onSubmit(event) {
      // Let every search form submit to Shopify's global product search.
      if (event.target.matches('[data-catalog-search]')) return;
      if (!event.target.matches('[data-catalog-add]')) return;
      event.preventDefault();
      const form = event.target;
      const button = form.querySelector('button');
      if (button.disabled) return;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      form.querySelector('[data-add-label]').textContent = 'Adding…';
      this.addQueue = this.addQueue.then(() => this.addProduct(form)).catch(() => {});
    }

    async addProduct(form) {
      const card = form.closest('[data-catalog-product]');
      const button = form.querySelector('button');
      const label = form.querySelector('[data-add-label]');
      const errorText = card.querySelector('[data-add-error]');
      errorText.hidden = true;
      let added = false;
      let confirmedFailure = false;
      try {
        const data = new FormData(form);
        data.append('sections', 'cart-drawer,cart-icon-bubble,catalog-cart-summary');
        data.append('sections_url', location.pathname);
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: data });
        const result = await response.json();
        if (!response.ok || result.status) {
          confirmedFailure = true;
          throw new Error(typeof result.description === 'string' ? result.description : 'This item could not be added. Please check its availability.');
        }
        added = true;
        ++this.summaryRevision;
        // A failed section render does not mean the add failed. Never repeat the POST.
        let sections = result.sections || {};
        if (['cart-drawer', 'cart-icon-bubble', 'catalog-cart-summary'].some(name => !sections[name])) {
          sections = { ...sections, ...await this.fetchSections(['cart-drawer', 'cart-icon-bubble', 'catalog-cart-summary']) };
        }
        this.renderCart(sections);
        label.textContent = 'Added';
        this.querySelector('[data-cart-status]').textContent = `${card.dataset.title} added to your cart.`;
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'catalog', productVariantId: form.elements.id.value, cartData: result }).catch(() => {});
        }
      } catch (error) {
        if (!confirmedFailure) this.drawerCurrent = false;
        errorText.textContent = added
          ? 'Added to your cart. Open Your cart to review it.'
          : confirmedFailure ? error.message : 'Could not confirm the add. Check your cart before trying again.';
        errorText.hidden = false;
        label.textContent = added ? 'Added' : 'Add to cart';
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (added) {
          const timer = setTimeout(() => {
            if (!button.disabled) label.textContent = 'Add to cart';
            this.timers.delete(timer);
          }, 1600);
          this.timers.add(timer);
        }
      }
    }

    async fetchSections(names) {
      const url = new URL(location.href);
      url.searchParams.delete('section_id');
      url.searchParams.set('sections', names.join(','));
      // Collection routes treat Accept: application/json as a request for the
      // collection JSON feed, bypassing section rendering entirely.
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) throw new Error('Cart preview unavailable');
      const sections = await response.json();
      if (names.some(name => !sections[name])) throw new Error('Missing cart section');
      return sections;
    }

    renderCart(sections) {
      const parser = new DOMParser();
      const drawer = document.querySelector('cart-drawer');
      if (drawer && sections['cart-drawer']) {
        const content = parser.parseFromString(sections['cart-drawer'], 'text/html').querySelector('#CartDrawer');
        if (!content) throw new Error('Missing cart drawer');
        drawer.querySelector('#CartDrawer').innerHTML = content.innerHTML;
        drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
      }
      if (sections['cart-icon-bubble']) {
        const icon = parser.parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
        const target = document.querySelector('#cart-icon-bubble');
        if (icon && target) target.innerHTML = icon.innerHTML;
      }
      if (sections['catalog-cart-summary']) {
        const summary = parser.parseFromString(sections['catalog-cart-summary'], 'text/html').querySelector('[data-catalog-cart-fragment]');
        if (!summary) throw new Error('Missing cart summary');
        this.querySelector('[data-catalog-cart]').replaceChildren(summary);
        this.updateCartBadges();
        drawer?.classList.toggle('is-empty', Number(summary.dataset.cartCount) === 0);
      }
      if (sections['cart-drawer']) this.drawerCurrent = true;
    }

    async refreshCartSummary() {
      const revision = ++this.summaryRevision;
      try {
        const sections = await this.fetchSections(['catalog-cart-summary']);
        if (this.isConnected && revision === this.summaryRevision) this.renderCart(sections);
      } catch (_) { /* Native cart remains available if the summary cannot refresh. */ }
    }

    updateCartBadges() {
      const tray = this.querySelector('[data-catalog-cart]');
      const summary = tray.querySelector('[data-catalog-cart-fragment]');
      tray.hidden = !summary || Number(summary.dataset.cartCount) === 0;
      let items = [];
      try { items = JSON.parse(tray.querySelector('[data-catalog-cart-items]')?.textContent || '[]'); } catch (_) { /* Empty summary. */ }
      const quantities = new Map();
      items.forEach(item => quantities.set(String(item.id), (quantities.get(String(item.id)) || 0) + item.quantity));
      this.cards.forEach(({ element }) => {
        const quantity = quantities.get(element.dataset.variantId) || 0;
        const badge = element.querySelector('[data-in-cart]');
        badge.hidden = quantity === 0;
        badge.textContent = `${quantity} in cart`;
      });
    }
  });
}
